import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Chip,
  Collapse,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ClearIcon from '@mui/icons-material/Clear';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import SearchIcon from '@mui/icons-material/Search';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import TableChartIcon from '@mui/icons-material/TableChart';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { useThemePreference, type ThemePreference } from '@/app/themePreference';
import { authedFetch } from '@/shared/api/client';
import { useAuth } from '@/shared/auth/AuthProvider';
import { ThemeToggle } from '@/shared/components/ThemeToggle';
import { TableThemeDialog } from '@/shared/components/TableThemeDialog';
import { SlipTemplateDialog } from '@/shared/components/SlipTemplateDialog';
import { NAV_SECTIONS, type NavItem, type NavSection } from './navigation';

const DRAWER_WIDTH = 272;
const COLLAPSED_DRAWER_WIDTH = 76;
const SIDEBAR_STORAGE_KEY = 'mh-wms-sidebar-collapsed';

async function loadEnvironment(): Promise<{ isStaging: boolean }> {
  const response = await authedFetch('/api/settings/environment');
  if (!response.ok) throw new Error('Environment lookup failed');
  return (await response.json()) as { isStaging: boolean };
}

function isActiveRoute(pathname: string, to: string): boolean {
  return to === '/' ? pathname === '/' : pathname === to || pathname.startsWith(`${to}/`);
}

function readSidebarPreference(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

function itemMatches(item: NavItem, section: NavSection, query: string): boolean {
  const searchable = [section.title, item.label, ...(item.keywords ?? [])]
    .join(' ')
    .toLowerCase();
  return searchable.includes(query);
}

export function AppLayout() {
  const { claims, signOut, can, canPermission } = useAuth();
  const { preference, setPreference } = useThemePreference();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopCollapsed, setDesktopCollapsed] = useState(readSidebarPreference);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [tableThemeOpen, setTableThemeOpen] = useState(false);
  const [slipTemplateOpen, setSlipTemplateOpen] = useState(false);
  const [isStaging, setIsStaging] = useState(false);
  const [search, setSearch] = useState('');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(NAV_SECTIONS.map((section) => [section.title, true])),
  );
  const desktopSearchInputRef = useRef<HTMLInputElement>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement>(null);
  const pathname = useRouterState({ select: (state) => state.location.pathname });

  useEffect(() => {
    let active = true;
    loadEnvironment()
      .then((environment) => {
        if (active) setIsStaging(environment.isStaging);
      })
      .catch(() => {
        // Environment identification is informational; navigation must remain available.
      });
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(SIDEBAR_STORAGE_KEY, String(desktopCollapsed));
    } catch {
      // A storage failure should not make navigation unusable.
    }
  }, [desktopCollapsed]);

  useEffect(() => {
    const focusSearch = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isTyping =
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable;
      if (
        isTyping ||
        (!((event.metaKey || event.ctrlKey) && event.key === 'k') && event.key !== '/')
      ) {
        return;
      }
      event.preventDefault();
      const desktop = window.matchMedia('(min-width: 900px)').matches;
      if (desktop) {
        setDesktopCollapsed(false);
      } else {
        setMobileOpen(true);
      }
      window.setTimeout(
        () => (desktop ? desktopSearchInputRef.current : mobileSearchInputRef.current)?.focus(),
        0,
      );
    };
    window.addEventListener('keydown', focusSearch);
    return () => window.removeEventListener('keydown', focusSearch);
  }, []);

  const permittedSections = useMemo(
    () =>
      NAV_SECTIONS.map((section) => ({
        ...section,
        items: section.items.filter(
          (item) => can(item.module) && (!item.permission || canPermission(item.permission)),
        ),
      })).filter((section) => section.items.length > 0),
    [can, canPermission],
  );

  const query = search.trim().toLowerCase();
  const filteredSections = permittedSections
    .map((section) => ({
      ...section,
      items: query
        ? section.items.filter((item) => itemMatches(item, section, query))
        : section.items,
    }))
    .filter((section) => section.items.length > 0);

  const activeItem = permittedSections
    .flatMap((section) => section.items)
    .filter((item) => isActiveRoute(pathname, item.to))
    .sort((left, right) => right.to.length - left.to.length)[0];
  const desktopWidth = desktopCollapsed ? COLLAPSED_DRAWER_WIDTH : DRAWER_WIDTH;
  const isAdmin = ['admin', 'administrator', 'superadmin', 'superadministrator'].includes(
    claims?.role?.trim().toLowerCase().replaceAll(' ', '') ?? '',
  );

  const openAccountMenu = (element: HTMLElement) => setMenuAnchor(element);
  const closeAccountMenu = () => setMenuAnchor(null);

  const renderNavItem = (item: NavItem, compact: boolean) => {
    const active = item.to === activeItem?.to;
    const button = (
      <ListItemButton
        key={item.to}
        component={Link}
        to={item.to}
        selected={active}
        aria-current={active ? 'page' : undefined}
        onClick={() => setMobileOpen(false)}
        sx={{
          minHeight: 44,
          mx: 1,
          my: 0.25,
          px: compact ? 1.25 : 1.5,
          borderRadius: 1.25,
          color: active ? 'primary.main' : 'text.secondary',
          justifyContent: compact ? 'center' : 'flex-start',
          borderLeft: '3px solid',
          borderLeftColor: active ? 'primary.main' : 'transparent',
          '& .MuiListItemIcon-root': { color: 'inherit' },
          '&.Mui-selected': { bgcolor: 'action.selected' },
          '&.Mui-selected:hover': { bgcolor: 'action.selected' },
          '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
        }}
      >
        <ListItemIcon sx={{ minWidth: compact ? 0 : 36, justifyContent: 'center' }}>
          {item.icon}
        </ListItemIcon>
        {!compact && (
          <ListItemText primary={item.label} primaryTypographyProps={{ fontWeight: 500 }} />
        )}
      </ListItemButton>
    );

    return compact ? (
      <Tooltip key={item.to} title={item.label} placement="right" arrow>
        {button}
      </Tooltip>
    ) : (
      button
    );
  };

  const drawerContent = (compact: boolean, mobile = false) => {
    const searchInputRef = mobile ? mobileSearchInputRef : desktopSearchInputRef;

    return (
      <Box
        sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
      >
        <Toolbar
          sx={{
            flexShrink: 0,
            px: compact ? 1 : 2,
            gap: 1,
            justifyContent: compact ? 'center' : 'space-between',
          }}
        >
          {!compact && (
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" noWrap fontWeight={700} color="text.primary">
                MH WMS
              </Typography>
              <Typography variant="caption" noWrap color="text.secondary">
                Warehouse management
              </Typography>
            </Box>
          )}
          <Tooltip title={compact ? 'Expand navigation' : 'Collapse navigation'} arrow>
            <IconButton
              onClick={() => setDesktopCollapsed((collapsed) => !collapsed)}
              aria-label={compact ? 'Expand navigation' : 'Collapse navigation'}
              sx={{
                display: { xs: 'none', md: 'inline-flex' },
                color: 'text.secondary',
                '&:hover': { color: 'text.primary', bgcolor: 'action.hover' },
              }}
            >
              {compact ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </IconButton>
          </Tooltip>
        </Toolbar>

        <Divider />

        {!compact && (
          <Box sx={{ p: 1.5, pb: 0.75 }}>
            <TextField
              inputRef={searchInputRef}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search navigation"
              inputProps={{ 'aria-label': 'Search navigation' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Clear navigation search"
                      onClick={() => {
                        setSearch('');
                        searchInputRef.current?.focus();
                      }}
                      sx={{ color: 'text.secondary' }}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: 'text.primary',
                  bgcolor: 'action.hover',
                  '& fieldset': { borderColor: 'divider' },
                  '&:hover fieldset': { borderColor: 'text.secondary' },
                  '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                },
                '& input::placeholder': { color: 'text.secondary', opacity: 1 },
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.5, px: 0.5 }}
            >
              Press / or Ctrl/⌘ K
            </Typography>
          </Box>
        )}

        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 0.75 }}>
          {filteredSections.length === 0 ? (
            <Typography
              color="text.secondary"
              variant="body2"
              sx={{ px: 2, py: 3, textAlign: 'center' }}
            >
              No permitted pages match “{search}”.
            </Typography>
          ) : (
            filteredSections.map((section, sectionIndex) => {
              const expandable =
                !compact && !query && section.collapsible !== false && section.items.length > 1;
              const expanded = Boolean(query) || expandedSections[section.title] !== false;

              return (
                <Box key={section.title} component="section" aria-label={section.title}>
                  {compact ? (
                    sectionIndex > 0 && <Divider sx={{ mx: 1.5, my: 0.75 }} />
                  ) : expandable ? (
                    <ListItemButton
                      onClick={() =>
                        setExpandedSections((current) => ({
                          ...current,
                          [section.title]: !expanded,
                        }))
                      }
                      aria-expanded={expanded}
                      aria-label={`${expanded ? 'Collapse' : 'Expand'} ${section.title}`}
                      sx={{
                        minHeight: 44,
                        mx: 1,
                        px: 1.5,
                        color: 'text.secondary',
                        borderRadius: 1,
                      }}
                    >
                      <ListItemText
                        primary={section.title}
                        primaryTypographyProps={{
                          variant: 'overline',
                          fontWeight: 700,
                          letterSpacing: 0.8,
                        }}
                      />
                      {expanded ? (
                        <ExpandLessIcon fontSize="small" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" />
                      )}
                    </ListItemButton>
                  ) : (
                    <Typography
                      variant="overline"
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        px: 2.5,
                        pt: 1.25,
                        pb: 0.25,
                        fontWeight: 700,
                        letterSpacing: 0.8,
                      }}
                    >
                      {section.title}
                    </Typography>
                  )}

                  <Collapse in={compact || expanded} timeout="auto" unmountOnExit={!compact}>
                    <List dense disablePadding>
                      {section.items.map((item) => (
                        <Box component="li" key={item.to} sx={{ listStyle: 'none' }}>
                          {renderNavItem(item, compact)}
                        </Box>
                      ))}
                    </List>
                  </Collapse>
                </Box>
              );
            })
          )}
        </Box>

        <Divider />
        <Box sx={{ p: 1 }}>
          <Tooltip
            title={compact ? `${claims?.email ?? 'Account'} — open account menu` : ''}
            placement="right"
            arrow
          >
            <ListItemButton
              onClick={(event) => openAccountMenu(event.currentTarget)}
              aria-label="Open account and appearance menu"
              aria-haspopup="menu"
              aria-expanded={Boolean(menuAnchor)}
              aria-controls={menuAnchor ? 'account-appearance-menu' : undefined}
              sx={{
                minHeight: 52,
                borderRadius: 1.25,
                px: compact ? 1 : 1.25,
                justifyContent: compact ? 'center' : 'flex-start',
                color: 'text.primary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  fontSize: 14,
                  bgcolor: 'primary.main',
                  flexShrink: 0,
                }}
              >
                {claims?.email?.[0]?.toUpperCase() ?? '?'}
              </Avatar>
              {!compact && (
                <Box sx={{ minWidth: 0, ml: 1.25 }}>
                  <Typography variant="body2" noWrap fontWeight={600}>
                    {claims?.email ?? 'Signed in user'}
                  </Typography>
                  <Typography variant="caption" noWrap color="text.secondary">
                    {claims?.role || 'No role'} ·{' '}
                    {preference === 'system' ? 'System theme' : `${preference} theme`}
                  </Typography>
                </Box>
              )}
            </ListItemButton>
          </Tooltip>
        </Box>
      </Box>
    );
  };

  const chooseTheme = (nextPreference: ThemePreference) => setPreference(nextPreference);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <Box
        component="a"
        href="#main-content"
        sx={{
          position: 'fixed',
          zIndex: (theme) => theme.zIndex.modal + 1,
          top: 8,
          left: 8,
          px: 2,
          py: 1.25,
          borderRadius: 1,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          fontWeight: 700,
          textDecoration: 'none',
          transform: 'translateY(-150%)',
          '&:focus': { transform: 'translateY(0)' },
        }}
      >
        Skip to main content
      </Box>

      <AppBar
        position="fixed"
        color="inherit"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${desktopWidth}px)` },
          ml: { md: `${desktopWidth}px` },
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper',
          transition: (theme) =>
            theme.transitions.create(['width', 'margin-left'], {
              duration: theme.transitions.duration.shorter,
            }),
        }}
      >
        <Toolbar sx={{ px: { xs: 1.5, sm: 2.5 } }}>
          <IconButton
            edge="start"
            onClick={() => setMobileOpen(true)}
            sx={{ mr: 1, display: { md: 'none' } }}
            aria-label="Open navigation"
            aria-expanded={mobileOpen}
          >
            <MenuIcon />
          </IconButton>
          <Typography variant="subtitle1" fontWeight={600} noWrap>
            {activeItem?.label ?? 'Warehouse management'}
          </Typography>
          {isStaging && (
            <Chip
              label="STAGING"
              size="small"
              color="warning"
              variant="filled"
              sx={{ ml: 1, height: 24, fontWeight: 800, letterSpacing: '0.06em' }}
            />
          )}
          <Box sx={{ flexGrow: 1 }} />
          <ThemeToggle />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ display: { xs: 'none', sm: 'block' } }}
          >
            {claims?.role || 'No role'}
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        aria-label="Primary navigation"
        sx={{ width: { md: desktopWidth }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              width: `min(${DRAWER_WIDTH}px, 88vw)`,
              boxSizing: 'border-box',
              bgcolor: 'background.paper',
              color: 'text.primary',
              borderRight: 1,
              borderColor: 'divider',
            },
          }}
        >
          {drawerContent(false, true)}
        </Drawer>
        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              width: desktopWidth,
              boxSizing: 'border-box',
              bgcolor: 'background.paper',
              color: 'text.primary',
              borderRight: 1,
              borderColor: 'divider',
              overflowX: 'hidden',
              transition: (theme) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.shorter,
                }),
            },
          }}
        >
          {drawerContent(desktopCollapsed)}
        </Drawer>
      </Box>

      <Box
        id="main-content"
        component="main"
        tabIndex={-1}
        sx={{
          flexGrow: 1,
          minWidth: 0,
          width: { md: `calc(100% - ${desktopWidth}px)` },
          p: { xs: 2, sm: 2.5, md: 3 },
        }}
      >
        <Toolbar />
        <Outlet />
      </Box>

      <Menu
        id="account-appearance-menu"
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={closeAccountMenu}
        MenuListProps={{ 'aria-label': 'Account and appearance' }}
        slotProps={{ paper: { sx: { minWidth: 240 } } }}
      >
        <MenuItem disabled sx={{ opacity: '1 !important', whiteSpace: 'normal' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>
              {claims?.email}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {claims?.role || 'No role'}
            </Typography>
          </Box>
        </MenuItem>
        <Divider />
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ display: 'block', px: 2, pt: 1 }}
        >
          Appearance
        </Typography>
        <ThemeMenuItem
          label="Light"
          value="light"
          selected={preference === 'light'}
          icon={<LightModeIcon fontSize="small" />}
          onSelect={chooseTheme}
        />
        <ThemeMenuItem
          label="Dark"
          value="dark"
          selected={preference === 'dark'}
          icon={<DarkModeIcon fontSize="small" />}
          onSelect={chooseTheme}
        />
        <ThemeMenuItem
          label="System"
          value="system"
          selected={preference === 'system'}
          icon={<SettingsBrightnessIcon fontSize="small" />}
          onSelect={chooseTheme}
        />
        {isAdmin && <Divider />}
        {isAdmin && (
          <MenuItem
            onClick={() => {
              closeAccountMenu();
              setTableThemeOpen(true);
            }}
          >
            <ListItemIcon>
              <TableChartIcon fontSize="small" />
            </ListItemIcon>
            Table appearance
          </MenuItem>
        )}
        {isAdmin && (
          <MenuItem
            onClick={() => {
              closeAccountMenu();
              setSlipTemplateOpen(true);
            }}
          >
            <ListItemIcon>
              <ReceiptLongIcon fontSize="small" />
            </ListItemIcon>
            Slip templates
          </MenuItem>
        )}
        <Divider />
        <MenuItem
          component="a"
          href="/login"
          onClick={() => {
            closeAccountMenu();
            signOut();
          }}
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
      <TableThemeDialog open={tableThemeOpen} onClose={() => setTableThemeOpen(false)} />
      {slipTemplateOpen && (
        <SlipTemplateDialog open onClose={() => setSlipTemplateOpen(false)} />
      )}
    </Box>
  );
}

interface ThemeMenuItemProps {
  label: string;
  value: ThemePreference;
  selected: boolean;
  icon: ReactNode;
  onSelect: (preference: ThemePreference) => void;
}

function ThemeMenuItem({ label, value, selected, icon, onSelect }: ThemeMenuItemProps) {
  return (
    <MenuItem
      role="menuitemradio"
      aria-checked={selected}
      selected={selected}
      onClick={() => onSelect(value)}
    >
      <ListItemIcon>{icon}</ListItemIcon>
      {label}
    </MenuItem>
  );
}
